<template>
  <div id="inventory-products" class="mt-3">
    <div class="data-container">
      <b-table
        v-dragscroll
        hover
        show-empty
        :items="inventoryData"
        :empty-text="isLoading ? 'Loading...' : 'No inventory found.'"
        :fields="fields"
        :current-page="pagination.currentPage"
        responsive
        class="p-0"
      >
        <template slot="HEAD_product_name" slot-scope="data">
          <div class="d-flex justify-content-between align-items-center">
            <span>{{ data.label }}</span>
            <div @click="sortProductName" class="cursor-pointer">
              <img
                v-if="productNameOrder === 'desc'"
                src="@/assets/images/icons/sort-up.svg"
                alt="Sort"
              />
              <img
                v-else-if="productNameOrder === 'asc'"
                src="@/assets/images/icons/sort-down.svg"
                alt="Sort"
              />
              <img v-else src="@/assets/images/icons/sort.svg" alt="Sort" />
            </div>
          </div>
        </template>
        <template slot="HEAD_decoded_variation_code" slot-scope="data">
          <b-checkbox v-model="isAllSelected" class="d-inline"> </b-checkbox>
          <span>{{ data.label }}</span>
        </template>
        <template
          v-if="isInventoryByProduct"
          slot="HEAD_branch_name"
          slot-scope="data"
        >
          <div class="d-flex justify-content-between align-items-center">
            <span>{{ data.label }}</span>
            <div @click="sortBranchName" class="cursor-pointer">
              <img
                v-if="branchNameOrder === 'desc'"
                src="@/assets/images/icons/sort-up.svg"
                alt="Sort"
              />
              <img
                v-else-if="branchNameOrder === 'asc'"
                src="@/assets/images/icons/sort-down.svg"
                alt="Sort"
              />
              <img v-else src="@/assets/images/icons/sort.svg" alt="Sort" />
            </div>
          </div>
        </template>
        <template slot="product_name" slot-scope="data">
          <div class="text-truncate" data-no-dragscroll>{{ data.value }}</div>
          <b-button
            v-if="data.item.add_on_category && data.item.add_on_category.length"
            variant="outline-primary"
            class="view-add-ons mt-3 py-1 px-2"
            @click="setProduct(data.item)"
          >
            <small class="font-weight-bold">Edit Add-Ons</small>
          </b-button>
        </template>
        <template slot="branch_name" slot-scope="data">
          <template v-if="data.value">
            <div class="text-truncate" data-no-dragscroll>
              {{ data.value }}
            </div>
          </template>
          <template v-else>
            <span class="text-muted">Branch Does Not Exist</span>
          </template>
        </template>
        <template slot="decoded_variation_code" slot-scope="data">
          <div class="d-flex">
          <b-checkbox
            v-model="selectedItems"
            :value="data.item.uuid"
            class="d-inline"
          >
          </b-checkbox>
          <div class="variation-container d-flex flex-wrap">
          <template v-if="data.value.length">
            <div
              v-for="(variation, index) in data.value"
              :key="index"
              class="pill"
                v-b-popover.hover="
                  variation.value
                "
              data-no-dragscroll
            >
              {{ variation.value.length<=10 ? variation.value : variation.value.slice(0,10)+"..." }}
            </div>
          </template>
          <template v-else>
            <span data-no-dragscroll>N/A</span>
          </template>
          </div>
          </div>
        </template>
        <template slot="regular_price" slot-scope="data">
          <b-form-group
            :invalid-feedback="getInvalidFeedback(data.index, 'regular_price')"
            :state="validateState(data.index, 'regular_price')"
          >
            <b-input-group prepend="₱">
              <currency-amount-input
                v-model="data.item.regular_price"
                @keyup.native="touchData(data)"
                class="border-left-0 pl-0"
                data-no-dragscroll
              />
            </b-input-group>
          </b-form-group>
        </template>
        <template slot="stock" slot-scope="data">
          <div class="d-flex justify-content-around align-items-baseline">
            <b-form-group
              :invalid-feedback="getInvalidFeedback(data.index, 'stock')"
              :state="validateState(data.index, 'stock')"
            >
              <numeric-input
                v-model="data.item.stock"
                className="stock-input"
                @keyup.native="touchData(data)"
                data-no-dragscroll
                :disabled="!data.item.is_limited_stock"
              />
            </b-form-group>

            <b-checkbox
              v-model="data.item.is_limited_stock"
              :value="false"
              :unchecked-value="true"
              class="ml-2"
              @input="touchData(data)"
              @change="
                (isLimitedStock) => {
                  if (!isLimitedStock) data.item.stock = null;
                }
              "
            >
              <small>Unlimited Stock</small>
              <i
                class="bi bi-question-circle ml-2 text-muted"
                aria-hidden="true"
                v-b-popover.hover="
                  'If checked, the stock quantity of the item will be unlimited.'
                "
              ></i>
            </b-checkbox>
          </div>
        </template>
        <template slot="discount_type" slot-scope="data">
          <b-dropdown
            variant="link"
            class="custom-dropdown"
            no-caret
            menu-class="w-100"
          >
            <template slot="button-content">
              <b-container>
                <b-row>
                  <b-col class="text-left pl-0">
                    {{
                      discountTypeOptions.find(
                        ({ value }) => value === data.value
                      ).text
                    }}
                  </b-col>
                  <b-col class="text-right pr-0">
                    <img src="@/assets/images/dropdown.svg" alt="Dropdown" />
                  </b-col>
                </b-row>
              </b-container>
            </template>
            <b-dropdown-item
              v-for="(option, index) in discountTypeOptions"
              :key="index"
              :value="option.value"
              @click="(data.item.discount_type = option.value), touchData(data)"
            >
              {{ option.text }}
            </b-dropdown-item>
          </b-dropdown>
        </template>
        <template slot="discount_value" slot-scope="data">
          <template v-if="data.item.discount_type === '%'">
            <b-form-group
              :invalid-feedback="
                getInvalidFeedback(data.index, 'discount_value')
              "
              :state="validateState(data.index, 'discount_value')"
            >
              <b-input-group append="%">
                <percentage-input
                  v-model="data.item.discount_value"
                  @keyup.native="touchData(data)"
                  class="border-right-0 pr-0"
                  data-no-dragscroll
                />
              </b-input-group>
            </b-form-group>
          </template>
          <template v-else-if="data.item.discount_type === 'PHP'">
            <b-form-group
              :invalid-feedback="
                getInvalidFeedback(data.index, 'discount_value')
              "
              :state="validateState(data.index, 'discount_value')"
            >
              <b-input-group prepend="₱">
                <currency-amount-input
                  v-model="data.item.discount_value"
                  @keyup.native="touchData(data)"
                  class="border-left-0 pl-0"
                  data-no-dragscroll
                />
              </b-input-group>
            </b-form-group>
          </template>
          <template v-else> N/A </template>
        </template>
        <template slot="sale_duration" slot-scope="data">
          <template v-if="data.item.discount_type">
            <b-form-group
              :invalid-feedback="getInvalidFeedback(data.index, 'on_sale_to')"
              :state="validateState(data.index, 'on_sale_to')"
              @click.native="touchData(data)"
            >
              <date-range-picker
                :value="data.item.sale_duration"
                :min-date="data.item.on_sale_from"
                @onDateRangeChange="
                  ({ from, to }) => {
                    data.item.on_sale_from = from;
                    data.item.on_sale_to = to;
                  }
                "
              />
            </b-form-group>
          </template>
          <template v-else> N/A </template>
        </template>
        <template slot="visibility" slot-scope="data">
          <div data-no-dragscroll>
            <switches
              v-model="data.item.visibility"
              @input="touchData(data)"
              theme="custom"
              color="primary"
              type-bold="true"
              class="toggle"
              :emit-on-mount="false"
            />
          </div>
        </template>
        <template slot="action" slot-scope="data">
          <b-btn
            variant="link"
            @click="saveChanges(data)"
            :disabled="isLoading"
          >
            Save Changes
          </b-btn>
        </template>
        <template slot="row-details" slot-scope="data">
          <div class="d-flex justify-content-center">
            <b-btn
              variant="outline-primary"
              class="btn-pill"
              :to="{
                name: 'ProductInventory',
                params: { productId: data.item.product_id },
              }"
            >
              View More
            </b-btn>
          </div>
        </template>
      </b-table>
    </div>
    <ViewAddOnsModal v-if="!isInventoryByProduct" :product="product" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import { mapFields } from "vuex-map-fields";
import { DateTime } from "luxon";
import ViewAddOnsModal from "./ViewAddOnsModal";
import CurrencyAmountInput from "@/components/common/CurrencyAmountInput";
import NumericInput from "@/components/common/NumericInput";
import PercentageInput from "@/components/common/PercentageInput";
import DateRangePicker from "@/components/common/DateRangePicker";
import Switches from "vue-switches";
import { dragscroll } from "vue-dragscroll";
import _ from "lodash";
import { required, requiredIf } from "vuelidate/lib/validators";
import notification from "@/services/notification";
import { greaterThanZero } from "@/validators";

export default {
  props: {
    isInventoryByProduct: {
      type: Boolean,
      default: false,
    },
  },
  directives: {
    dragscroll,
  },
  components: {
    ViewAddOnsModal,
    CurrencyAmountInput,
    NumericInput,
    PercentageInput,
    DateRangePicker,
    Switches,
  },
  data() {
    return {
      productNameOrder: "",
      branchNameOrder: "",
      fields: [
        {
          key: "product_name",
          tdClass: (value) => {
            if (value)
              return [
                "border-left-0",
                "border-right-0",
                "border-bottom-0",
                "border-top",
              ];
            return "border-0";
          },
        },
        {
          key: "branch_name",
          label: "Branch",
        },
        { key: "decoded_variation_code", label: "Variation" },
        {
          key: "regular_price",
          label: "Price",
        },
        { key: "stock" },
        {
          key: "discount_type",
          label: "Sale Type",
        },
        {
          key: "discount_value",
          label: "Sale Value",
        },
        {
          key: "sale_duration",
        },
        {
          key: "visibility",
        },
        {
          key: "action",
        },
      ],
      discountTypeOptions: [
        { text: "Percentage", value: "%" },
        { text: "Php", value: "PHP" },
        { text: "N/A", value: "" },
      ],
      product: null,
      inventoryData: [],
    };
  },
  computed: {
    ...mapState("products", ["pagination", "isLoading", "errors"]),
    ...mapFields("products", ["inventoryList"]),
    ...mapFields("products", ["isAllSelected", "selectedItems"]),
  },
  validations: {
    inventoryData: {
      required,
      $each: {
        regular_price: {
          required,
        },
        stock: {
          required: requiredIf(({ is_limited_stock }) => is_limited_stock),
        },
        discount_value: {
          required: requiredIf(({ discount_type }) => Boolean(discount_type)),
          greaterThanZero: (value, item) => {
            if (!item.discount_type) return true;

            return greaterThanZero(value);
          },
        },
        on_sale_to: {
          required: requiredIf(({ discount_type }) => discount_type),
        },
      },
    },
  },
  watch: {
    inventoryList: {
      deep: true,
      handler() {
        const inventoryData = [];
        const formatDate = (value) =>
          DateTime.fromSQL(value).toFormat("yyyy-LL-dd");
        const isValidDateTime = (value) =>
          value && DateTime.fromSQL(value).isValid;

        this.inventoryList.forEach((item) => {
          item.inventories.map((inventory, index) => {
            inventory.is_limited_stock = Boolean(inventory.is_limited_stock);
            inventory.on_sale = Boolean(inventory.on_sale);
            inventory.visibility = Boolean(inventory.visibility);
            inventory.sale_duration = "";
            inventory.discount_value =
              inventory.discount_value && inventory.discount_type === "%"
                ? parseInt(inventory.discount_value)
                : inventory.discount_value;

            if (
              isValidDateTime(inventory.on_sale_from) &&
              isValidDateTime(inventory.on_sale_to)
            ) {
              inventory.on_sale_from = formatDate(inventory.on_sale_from);
              inventory.on_sale_to = formatDate(inventory.on_sale_to);
              inventory.sale_duration = `${inventory.on_sale_from} to ${inventory.on_sale_to}`;
            } else {
              inventory.on_sale_from = null;
              inventory.on_sale_to = null;
            }

            if (!this.isInventoryByProduct) {
              inventory.count = item.inventory_count;

              if (index === 0) {
                inventory.product_name = item.product_name;
                inventory.add_on_category = item.add_on_category;
              }

              if (
                index === item.inventories.length - 1 &&
                item.inventories.length !== inventory.count
              )
                inventory._showDetails = true;
            }
          });
          inventoryData.push(...item.inventories);
        });
        this.inventoryData = inventoryData;
      },
    },
    isAllSelected(value) {
      if (value)
        this.selectedItems = this.inventoryData.map(({ uuid }) => uuid);
      else this.selectedItems = [];
    },
  },
  methods: {
    setProduct(product) {
      product = JSON.parse(JSON.stringify(product));
      let add_on_category = [];
      product.add_on_category.forEach((category) => {
        category['allow_multiple_selection'] = category.limit > 1;
        add_on_category.push(category);
      });
      product.add_on_category = add_on_category;
      this.product = product;
      this.$root.$emit("bv::show::modal", "inventory-view-add-ons-modal");
    },
    touchData(data) {
      if (!this.selectedItems.includes(data.item.uuid))
        this.selectedItems.push(data.item.uuid);

      this.$v.inventoryData.$each[data.index].$reset();
    },
    saveChanges(data) {
      this.$v.inventoryData.$each[data.index].$touch();

      if (this.$v.inventoryData.$each[data.index].$invalid)
        notification.warning("Oops, cannot update product inventory.");
      else this.updateInventory([data.item]);
    },
    formatPayload(items) {
      const inventory = [];

      items.forEach((item) => {
        let discountValue = item.discount_value ? item.discount_value : 0;

        if (item.discount_type === "PHP")
          parseFloat(discountValue.replaceAll(",", ""));
        if (item.discount_type === "%") parseInt(discountValue);

        inventory.push({
          inventory_uuid: item.uuid,
          currency: item.currency,
          regular_price: parseFloat(item.regular_price.replaceAll(",", "")),
          discount_type: item.discount_type ? item.discount_type : null,
          discount_value: discountValue,
          on_sale: Boolean(item.discount_type),
          on_sale_from:
            item.discount_type && item.on_sale_from
              ? `${item.on_sale_from} 00:00:00`
              : null,
          on_sale_to:
            item.discount_type && item.on_sale_to
              ? `${item.on_sale_to} 23:59:59`
              : null,
          is_visible: item.visibility,
          stock: item.is_limited_stock ? parseInt(item.stock) : null,
          is_limited_stock: item.is_limited_stock,
        });
      });

      return inventory;
    },
    async updateInventory(items) {
      const inventory = this.formatPayload(items);

      if (inventory.length) {
        await this.$store.dispatch("products/updateInventory", { inventory });
        if (_.isEmpty(this.errors)) {
          this.$emit("getInventoryList");
          this.selectedItems = [];
          this.isAllSelected = false;
          this.$v.inventoryData.$reset();
        }
      }
    },
    sortProductName() {
      if (this.productNameOrder === "asc") this.productNameOrder = "desc";
      else this.productNameOrder = "asc";

      this.inventoryList = _.orderBy(
        this.inventoryList,
        "product_name",
        this.productNameOrder
      );
    },
    sortBranchName() {
      if (this.branchNameOrder === "asc") this.branchNameOrder = "desc";
      else this.branchNameOrder = "asc";

      this.inventoryData = _.orderBy(
        this.inventoryData,
        "branch_name",
        this.branchNameOrder
      );
    },
    getInvalidFeedback(index, key) {
      const field = this.$v.inventoryData.$each[index][key];

      if (field.hasOwnProperty("required") && !field.required)
        return "This field is required.";

      if (field.hasOwnProperty("greaterThanZero") && !field.greaterThanZero)
        return "Must be greater than 0.";

      return "";
    },
    validateState(index, key) {
      const { $dirty, $error } = this.$v.inventoryData.$each[index][key];

      return $dirty ? !$error : null;
    },
  },
  created() {
    if (this.isInventoryByProduct)
      this.fields = this.fields.filter(({ key }) => key !== "product_name");
  },
  mounted() {
    this.$root.$on("onUpdateInventory", async () => {
      if (this.inventoryData.length) {
        const items = [];
        const indexes = [];

        this.inventoryData.forEach((item, index) => {
          if (this.selectedItems.includes(item.uuid)) {
            this.$v.inventoryData.$each[index].$touch();
            items.push(item);
            indexes.push(index);
          }
        });
        const isInvalid = indexes.some(
          (index) => this.$v.inventoryData.$each[index].$invalid
        );

        if (isInvalid)
          notification.warning("Oops, cannot update product inventory.");
        else {
          await this.updateInventory(items);
        }
      }
    });
  },
  beforeDestroy() {
    this.$root.$off("onUpdateInventory");
  },
};
</script>

<style lang="scss">
#inventory-products .data-container {
  overflow: hidden;
  .form-control:focus {
    border-color: #ced4da;
  }
  .table-responsive {
    height: 65vh;
    background: #fff;
  }
  table {
    overflow-y: auto;
    margin: 0 !important;
    thead {
      position: sticky;
      top: -1px;
      z-index: 9;
      th {
        &:first-child {
          border-top-left-radius: 10px;
          border-bottom-left-radius: 0;
        }
        &:last-child {
          border-top-right-radius: 10px;
          border-bottom-right-radius: 0;
        }
      }
    }
    tbody {
      &:before {
        display: none;
      }
      tr {
        font-size: 14px;
        box-shadow: none;
        &:first-child > td:first-child {
          border-top-left-radius: 0;
        }
        &:last-child > td:first-child {
          border-bottom-left-radius: 10px;
        }
      }
      td {
        min-width: 150px;
        max-width: 300px;
        vertical-align: top;
        &:nth-child(9) {
          min-width: 90px;
        }
      }
      .input-group-prepend .input-group-text,
      .input-group-append .input-group-text {
        background-color: #ffffff;
        color: #88939d;
      }
      .stock-input {
        width: 120px;
      }
      .toggle {
        top: 15px;
      }
    }
  }
  .table-hover tbody tr:hover {
    background-color: #f7fafd;
  }
  .view-add-ons {
    border-radius: 20px;
  }
  .custom-dropdown {
    width: 180px;
    button {
      background: #ffffff;
      color: #2f3941;
      border-color: #ced4da;
      width: 100%;
      text-decoration: none;
    }
    .dropdown-item:active {
      background-color: #ffffff;
      color: #2f3941;
    }
  }
  .pill {
    display: inline-block;
    border-radius: 13px;
    padding: 1px 8px;
    background-color: #f9f9f9;
    min-width: 50px;
    margin-right: 10px;
    border: 1px solid #f1f2f2;
    text-align: center;
  }
  .variation-container{
    display: inline-block;
    width:225px;
  }
}
</style>
